import { useAuth } from "@/auth/AuthProvider";
import { selectedWorkspaceOption } from "@/auth/workspaceOptions";
import { SBCard } from "@/components/ui";
import { useWebMCPBridgePreference } from "@/features/ai/webmcp/webmcpPreference";
import { canShowAITab } from "@/models/user";
import "@/features/account/account.css";
import "@/features/home/home.css";

// User-level app settings: per-window workspace sync behavior and the
// per-window external-agent (WebMCP) bridge.

export function SettingsView() {
  const { currentUser, workspaceOptions, isWorkspaceUntethered, setWorkspaceUntethered, isSBotLabsAdmin } = useAuth();
  const [isBridgeEnabled, setBridgeEnabled] = useWebMCPBridgePreference(currentUser?.id);
  if (!currentUser) return null;

  const canUseBridge = canShowAITab(currentUser, isSBotLabsAdmin);

  const selected = selectedWorkspaceOption(currentUser, workspaceOptions);
  const pinnedName = selected?.displayTitle ?? currentUser.activeContext?.displayName ?? "its current workspace";
  const canUntether = isWorkspaceUntethered || Boolean(currentUser.activeContext);

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>Settings</h1>
      </div>

      <SBCard className="home-card">
        <h3>Workspace sync</h3>
        <label style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14 }}>
          <input
            type="checkbox"
            checked={isWorkspaceUntethered}
            disabled={!canUntether}
            onChange={(e) => setWorkspaceUntethered(e.target.checked)}
            style={{ width: "auto" }}
          />
          Untether this window from workspace sync
        </label>
        <p className="home-hint">
          {isWorkspaceUntethered
            ? `This window is pinned to ${pinnedName}. Switching workspaces here won't affect your other windows or devices, and their changes won't affect this window. The pin lasts until you retether, sign out, or close this window.`
            : "Your selected workspace follows your account across windows and devices. Untether to work in a different workspace in this window only — for example, two projects in two browser windows."}
        </p>
        {!canUntether && <p className="home-hint">Select a workspace first to enable untethering.</p>}
      </SBCard>

      <SBCard className="home-card">
        <h3>External agent bridge (WebMCP)</h3>
        <label style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14 }}>
          <input
            type="checkbox"
            checked={isBridgeEnabled}
            disabled={!canUseBridge}
            onChange={(e) => setBridgeEnabled(e.target.checked)}
            style={{ width: "auto" }}
          />
          Expose SBot AI tools to a browser agent in this window
        </label>
        <p className="home-hint">
          {isBridgeEnabled
            ? "An agent you run yourself (for example through Playwright MCP or a WebMCP-capable browser) can now discover and call the same tools as the SBot assistant, as you, in this window's workspace. Agents can only stage changes: each one appears here for you to approve or reject. The bridge lasts until you turn it off, sign out, or close this window."
            : "Off by default. When on, an AI agent you bring yourself can use the SBot assistant's tools on this page with your permissions. Nothing is changed without your approval in this window."}
        </p>
        {!canUseBridge && <p className="home-hint">The AI tools are not available for your current role.</p>}
      </SBCard>
    </div>
  );
}
